import type { AuthContext } from '../api/client'
import type { SessionResult } from '../api/sessions'
import type { User } from '../api/users'
import { deleteCookie, readCookie, writeCookie } from './cookies'
import { createSharedStore } from './create-store'

/**
 * The signed-in session, shared by every mounted component.
 *
 * Cookie names match what the Codeverse API already accepts as fallbacks for
 * the Authorization / Current-User-Id headers, so any other Codeverse property
 * on the domain picks up the session for free.
 */
export const ACCESS_TOKEN_COOKIE = 'access_token'
export const USER_ID_COOKIE = 'current_user_id'
export const SESSION_META_COOKIE = 'cv_session'

/**
 * The user snapshot is cached in localStorage rather than a cookie on purpose:
 * cookies are sent on every request to every subdomain, and a full user object
 * there would be dead weight on each one. It is only a render cache — the
 * cookies remain the source of truth for whether a session exists.
 */
export const USER_CACHE_KEY = 'cv.user'

export type SessionStatus = 'authenticated' | 'anonymous'

interface SessionMeta {
  /** Epoch milliseconds. */
  expiresAt: number | null
}

export interface SessionState {
  status: SessionStatus
  accessToken: string | null
  userId: string | null
  expiresAt: number | null
  user: User | null

  signIn(result: SessionResult): void
  signOut(): void
  setUser(user: User): void
}

const ANONYMOUS = {
  status: 'anonymous',
  accessToken: null,
  userId: null,
  expiresAt: null,
  user: null,
} as const

function parseJson<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function readUserCache(): User | null {
  try {
    return parseJson<User>(localStorage.getItem(USER_CACHE_KEY))
  } catch {
    // localStorage throws in private-mode Safari and when storage is blocked.
    return null
  }
}

function writeUserCache(user: User | null): void {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_CACHE_KEY)
  } catch {
    // A missing render cache is harmless; never let it break sign-in.
  }
}

function clearStorage(): void {
  deleteCookie(ACCESS_TOKEN_COOKIE)
  deleteCookie(USER_ID_COOKIE)
  deleteCookie(SESSION_META_COOKIE)
  writeUserCache(null)
}

type StoredSession = Omit<SessionState, 'signIn' | 'signOut' | 'setUser'>

/**
 * Read the session from cookies. Runs synchronously at module load so the
 * first paint already knows whether someone is signed in — no flash of the
 * signed-out button on every page of the marketing site.
 */
export function readStoredSession(): StoredSession {
  const accessToken = readCookie(ACCESS_TOKEN_COOKIE)
  const userId = readCookie(USER_ID_COOKIE)
  if (!accessToken || !userId) return { ...ANONYMOUS }

  const meta = parseJson<SessionMeta>(readCookie(SESSION_META_COOKIE))
  const expiresAt = meta?.expiresAt ?? null

  // An expired token is indistinguishable from being signed out, and leaving
  // the cookies in place would make every API call fail with a 401.
  if (expiresAt !== null && expiresAt <= Date.now()) {
    clearStorage()
    return { ...ANONYMOUS }
  }

  return {
    status: 'authenticated',
    accessToken,
    userId,
    expiresAt,
    user: readUserCache(),
  }
}

function persist(result: SessionResult): number {
  const maxAgeSeconds = result.expiresInSeconds
  const expiresAt = Date.now() + maxAgeSeconds * 1000

  writeCookie(ACCESS_TOKEN_COOKIE, result.accessToken, { maxAgeSeconds })
  writeCookie(USER_ID_COOKIE, result.userId, { maxAgeSeconds })
  writeCookie(SESSION_META_COOKIE, JSON.stringify({ expiresAt } satisfies SessionMeta), {
    maxAgeSeconds,
  })
  writeUserCache(result.user)

  return expiresAt
}

export const session = createSharedStore<SessionState>((set) => ({
  ...readStoredSession(),

  signIn(result) {
    const expiresAt = persist(result)
    set({
      status: 'authenticated',
      accessToken: result.accessToken,
      userId: result.userId,
      expiresAt,
      user: result.user,
    })
  },

  /**
   * Local only. The API exposes no revoke endpoint — the session resource
   * reports `meta.deletable: false` — so signing out means dropping our copy
   * of the token and letting it expire on its own.
   */
  signOut() {
    clearStorage()
    set({ ...ANONYMOUS })
  },

  setUser(user) {
    writeUserCache(user)
    set({ user })
  },
}))

export const useSession = session.useStore

/**
 * Sign out and leave for `url`. Every logout control goes through here so an
 * account page is never left showing content the visitor can no longer see.
 */
export function signOutTo(url: string): void {
  session.get().signOut()
  window.location.assign(url)
}

/** Auth context for `apiFetch`, or null when signed out. */
export function currentAuth(): AuthContext | null {
  const state = session.get()
  if (state.status !== 'authenticated' || !state.accessToken || !state.userId) return null
  return { accessToken: state.accessToken, userId: state.userId }
}

/** Re-read cookies into the store. Used by tests and after cross-tab changes. */
export function rehydrateSession(): void {
  session.set(readStoredSession())
}
